import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, from, switchMap } from 'rxjs';
import { ApiConfigurationService } from '@core/services/api-configuration.service';
import { environment } from 'src/environments/environment';

export interface SalesLead {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  leadScore: number;
  registrationPath: string;
  registrationDate: string;
  priority: 'HIGH' | 'MEDIUM' | 'NORMAL';
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'CLOSED';
  tier: 'FREE' | 'TRIAL' | 'PREMIUM';
  sourceUrl?: string;
  lastContactDate?: string;
  notes?: string;
}

export interface LeadMetrics {
  totalLeads: number;
  highPriorityLeads: number;
  mediumPriorityLeads: number;
  normalPriorityLeads: number;
  todayLeads: number;
  weekLeads: number;
  monthLeads: number;
  conversionRate: number;
  averageLeadScore: number;
  responseTime: number; // hours
}

export interface LeadTrend {
  date: string;
  leadCount: number;
  averageScore: number;
  conversions: number;
}

@Injectable({
  providedIn: 'root',
})
export class SalesDashboardService {
  private http = inject(HttpClient);
  private apiConfigService = inject(ApiConfigurationService);

  // Real-time lead updates
  private leadsSubject = new BehaviorSubject<SalesLead[]>([]);
  public leads$ = this.leadsSubject.asObservable();

  private metricsSubject = new BehaviorSubject<LeadMetrics | null>(null);
  public metrics$ = this.metricsSubject.asObservable();

  private apiBaseUrl = `${environment.azureFunctions.alertsUrl}/api`;

  constructor() {
    // Start real-time updates
    this.startRealTimeUpdates();
  }

  /**
   * Fetches sales leads with filtering, pagination, and search
   *
   * @param filters - Optional filters (priority, status, dateRange, searchTerm, page, limit)
   * @returns Observable emitting { leads: SalesLead[], total: number }
   * - Observable semantics: Emits once then completes
   * - HTTP errors: Propagates to error observer (no error handling)
   * - State touched: None (read-only, updates handled by polling)
   * - Idempotency: Safe to call repeatedly
   * - API endpoint: GET /api/sales_leads with query params
   * - Pagination: page and limit params control result set
   */
  getLeads(filters?: {
    priority?: string;
    status?: string;
    dateRange?: { start: string; end: string };
    searchTerm?: string;
    page?: number;
    limit?: number;
  }): Observable<{ leads: SalesLead[]; total: number }> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          if (typeof value === 'object') {
            params.append(key, JSON.stringify(value));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.get<{ leads: SalesLead[]; total: number }>(
          `${this.apiBaseUrl}/sales_leads?${params.toString()}`,
          {
            headers: {
              'x-functions-key': token.value,
            },
          },
        )
      )
    );
  }

  /**
   * Get lead metrics and KPIs
   */
  getLeadMetrics(): Observable<LeadMetrics> {
    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.get<LeadMetrics>(`${this.apiBaseUrl}/sales_metrics`, {
          headers: {
            'x-functions-key': token.value,
          },
        })
      )
    );
  }

  /**
   * Get lead trends over time
   */
  getLeadTrends(days: number = 30): Observable<LeadTrend[]> {
    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.get<LeadTrend[]>(
          `${this.apiBaseUrl}/sales_trends?days=${days}`,
          {
            headers: {
              'x-functions-key': token.value,
            },
          },
        )
      )
    );
  }

  /**
   * Updates lead status and adds contact timestamp
   *
   * @param leadId - Lead unique identifier
   * @param status - New status (NEW|CONTACTED|QUALIFIED|CONVERTED|CLOSED)
   * @param notes - Optional notes about status change
   * @returns Observable emitting updated SalesLead
   * - Observable semantics: Emits once then completes
   * - HTTP errors: Propagates to error observer
   * - State touched: None directly (polling will refresh leads)
   * - Idempotency: Safe to call with same status (creates audit trail)
   * - API endpoint: PATCH /api/sales_leads/{leadId}
   * - Side effect: Sets lastContactDate to current ISO timestamp
   */
  updateLeadStatus(
    leadId: string,
    status: SalesLead['status'],
    notes?: string,
  ): Observable<SalesLead> {
    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.patch<SalesLead>(
          `${this.apiBaseUrl}/sales_leads/${leadId}`,
          { status, notes, lastContactDate: new Date().toISOString() },
          {
            headers: {
              'x-functions-key': token.value,
              'Content-Type': 'application/json',
            },
          },
        )
      )
    );
  }

  /**
   * Add notes to a lead
   */
  addLeadNote(leadId: string, note: string): Observable<SalesLead> {
    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.post<SalesLead>(
          `${this.apiBaseUrl}/sales_leads/${leadId}/notes`,
          { note, timestamp: new Date().toISOString() },
          {
            headers: {
              'x-functions-key': token.value,
              'Content-Type': 'application/json',
            },
          },
        )
      )
    );
  }

  /**
   * Get lead details with interaction history
   */
  getLeadDetails(
    leadId: string,
  ): Observable<SalesLead & { interactions: any[] }> {
    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.get<SalesLead & { interactions: any[] }>(
          `${this.apiBaseUrl}/sales_leads/${leadId}`,
          {
            headers: {
              'x-functions-key': token.value,
            },
          },
        )
      )
    );
  }

  /**
   * Sends automated follow-up email to lead based on template
   *
   * @param leadId - Lead unique identifier
   * @param templateType - Email template key (welcome|demo|trial|custom)
   * @param customMessage - Optional message for 'custom' template type
   * @returns Observable emitting { status: string, messageId: string }
   * - Observable semantics: Emits once then completes
   * - HTTP errors: Propagates to error observer
   * - State touched: None (email service handles delivery)
   * - Idempotency: NOT safe - sends duplicate email on each call
   * - API endpoint: POST /api/sales_follow_up
   * - Side effect: Triggers email delivery via external service
   */
  sendFollowUpEmail(
    leadId: string,
    templateType: 'welcome' | 'demo' | 'trial' | 'custom',
    customMessage?: string,
  ): Observable<{ status: string; messageId: string }> {
    return from(this.apiConfigService.getAlertsFunctionToken()).pipe(
      switchMap(token =>
        this.http.post<{ status: string; messageId: string }>(
          `${this.apiBaseUrl}/sales_follow_up`,
          {
            leadId,
            templateType,
            customMessage,
            timestamp: new Date().toISOString(),
          },
          {
            headers: {
              'x-functions-key': token.value,
              'Content-Type': 'application/json',
            },
          },
        )
      )
    );
  }

  /**
   * Start real-time updates via polling (could be WebSocket in production)
   */
  private startRealTimeUpdates(): void {
    // Poll for new leads every 30 seconds
    setInterval(() => {
      this.refreshDashboard();
    }, 30000);

    // Initial load
    this.refreshDashboard();
  }

  /**
   * Refresh dashboard data
   */
  refreshDashboard(): void {
    // Get latest leads
    this.getLeads({ limit: 50 }).subscribe({
      next: (response) => {
        this.leadsSubject.next(response.leads);
      },
      error: (error) => {
        console.error('Failed to refresh leads:', error);
      },
    });

    // Get latest metrics
    this.getLeadMetrics().subscribe({
      next: (metrics) => {
        this.metricsSubject.next(metrics);
      },
      error: (error) => {
        console.error('Failed to refresh metrics:', error);
      },
    });
  }

  /**
   * Calculate lead priority based on score
   */
  calculatePriority(leadScore: number): 'HIGH' | 'MEDIUM' | 'NORMAL' {
    if (leadScore >= 80) return 'HIGH';
    if (leadScore >= 60) return 'MEDIUM';
    return 'NORMAL';
  }

  /**
   * Get priority color class
   */
  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'HIGH':
        return 'danger';
      case 'MEDIUM':
        return 'warning';
      case 'NORMAL':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  /**
   * Get status color class
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'NEW':
        return 'primary';
      case 'CONTACTED':
        return 'info';
      case 'QUALIFIED':
        return 'warning';
      case 'CONVERTED':
        return 'success';
      case 'CLOSED':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  /**
   * Format lead score display
   */
  formatLeadScore(score: number): string {
    return `${score}/100`;
  }

  /**
   * Calculate time since registration
   */
  getTimeSinceRegistration(registrationDate: string): string {
    const now = new Date();
    const regDate = new Date(registrationDate);
    const diffMs = now.getTime() - regDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  /**
   * Calculates recommended next action based on lead status and timing
   *
   * @param lead - SalesLead object to analyze
   * @returns Human-readable action recommendation string
   * - Decision tree:
   *   - NEW + HIGH priority + >2hr → "URGENT: Contact immediately"
   *   - NEW + MEDIUM priority + >24hr → "Contact within next few hours"
   *   - NEW + >72hr → "Add to nurture sequence"
   *   - CONTACTED → "Follow up on initial contact"
   *   - QUALIFIED → "Schedule demo or trial"
   *   - Default → "Continue nurturing"
   * - Use case: Display in dashboard, trigger automated workflows
   */
  getRecommendedAction(lead: SalesLead): string {
    const timeSinceReg =
      new Date().getTime() - new Date(lead.registrationDate).getTime();
    const hoursAgo = timeSinceReg / (1000 * 60 * 60);

    if (lead.status === 'NEW') {
      if (lead.priority === 'HIGH' && hoursAgo > 2) {
        return 'URGENT: Contact immediately - high priority lead';
      } else if (lead.priority === 'MEDIUM' && hoursAgo > 24) {
        return 'Contact within next few hours';
      } else if (hoursAgo > 72) {
        return 'Add to nurture sequence';
      } else {
        return 'Schedule follow-up call';
      }
    } else if (lead.status === 'CONTACTED') {
      return 'Follow up on initial contact';
    } else if (lead.status === 'QUALIFIED') {
      return 'Schedule demo or trial';
    }

    return 'Continue nurturing';
  }
}
